<template>
    <b-card
            :header="$t('Overview')"
            tag="section"
            class="mb-3"
    >
        <b-form class="mb-3">
            <b-form-group
                    :label="$t('Search')"
                    :label-for="filter"
            >
                <b-form-input
                        id="filter"
                        v-model="filter"
                        autofocus
                />
            </b-form-group>
        </b-form>
        <b-table
                id="overview-table"
                :items="fetchData"
                :fields="fields"
                :filter="filter"
                no-provider-filtering
        >
            <template v-for="field in fields" :slot="'HEAD_' + field" slot-scope="data">
                {{ $t(data.column) }}
            </template>

            <template
                    v-if="data.item.project_name"
                    slot="project_name"
                    slot-scope="data"
            >
                <b-link :to="{ name: 'projects.show', params: { projectId: data.item.project_id } }">
                    {{ data.item.project_name }}
                </b-link>
            </template>

            <template
                    v-if="data.item.component_name"
                    slot="component_name"
                    slot-scope="data"
            >
                <b-link :to="{ name: 'projects.components.show', params: { projectId: data.item.project_id, componentId: data.item.component_id } }">
                    {{ data.item.component_name }}
                </b-link>
            </template>

            <template
                    v-if="data.item.floor_name"
                    slot="floor_name"
                    slot-scope="data"
            >
                <b-link :to="{ name: 'projects.components.floors.show', params: { projectId: data.item.project_id, componentId: data.item.component_id, floorId: data.item.floor_id } }">
                    {{ data.item.floor_name }}
                </b-link>

                <b-form
                        :action="`/api/projects/${data.item.project_id}/components/${data.item.component_id}/floors/${data.item.floor_id}`"
                        class="mt-3"
                        @change.prevent="handleUpdate"
                        @submit.prevent="handleUpdate"
                >
                    <b-form-group
                            :label="$t('Height')"
                    >
                        <b-form-input
                                :value="data.item.floor_height"
                                name="height"
                                type="number"
                                step="0.001"
                                @keypress.enter="goToNextEmptyInput($event.target)"
                        />
                    </b-form-group>
                </b-form>
            </template>

            <template
                    v-if="data.item.room_name"
                    slot="room_name"
                    slot-scope="data"
            >
                <b-link :to="{ name: 'projects.components.floors.rooms.show', params: { projectId: data.item.project_id, componentId: data.item.component_id, floorId: data.item.floor_id, roomId: data.item.room_id } }">
                    {{ data.item.room_name }}
                </b-link>

                <b-form
                        :action="`/api/projects/${data.item.project_id}/components/${data.item.component_id}/floors/${data.item.floor_id}/rooms/${data.item.room_id}`"
                        class="mt-3"
                        @change.prevent="handleUpdate"
                        @submit.prevent="handleUpdate"
                >
                    <b-form-group
                            :label="$t('Height')"
                    >
                        <b-form-input
                                :value="data.item.room_height"
                                name="height"
                                type="number"
                                step="0.001"
                                @keypress.enter="goToNextEmptyInput($event.target)"
                        />
                    </b-form-group>
                </b-form>
            </template>

            <template
                    v-if="data.item.wall_name"
                    slot="wall_name"
                    slot-scope="data"
            >
                <b-link :to="{ name: 'projects.components.floors.rooms.walls.show', params: { projectId: data.item.project_id, componentId: data.item.component_id, floorId: data.item.floor_id, roomId: data.item.room_id, wallId: data.item.wall_id } }">
                    {{ data.item.wall_name }}
                </b-link>

                <b-form
                        :action="`/api/projects/${data.item.project_id}/components/${data.item.component_id}/floors/${data.item.floor_id}/rooms/${data.item.room_id}/walls/${data.item.wall_id}`"
                        class="mt-3"
                        @change.prevent="handleUpdate"
                        @submit.prevent="handleUpdate"
                >
                    <b-form-group
                            :label="$t('Height')"
                    >
                        <b-form-input
                                :value="data.item.wall_height"
                                name="height"
                                type="number"
                                step="0.001"
                                @keypress.enter="goToNextEmptyInput($event.target)"
                        />
                    </b-form-group>

                    <b-form-group
                            :label="$t('Width')"
                    >
                        <b-form-input
                                :value="data.item.wall_width"
                                name="width"
                                type="number"
                                step="0.001"
                                @keypress.enter="goToNextEmptyInput($event.target)"
                        />
                    </b-form-group>
                </b-form>
            </template>

            <template slot="element_name" slot-scope="data">
                <span v-html="data.item.element_name"></span>
                <template v-if="data.item.element_count">
                    (&times; {{ data.item.element_count }})
                </template>
            </template>
        </b-table>
    </b-card>
</template>

<script>
  export default {
    name: 'Overview',
    props: {
      apiUrl: {
        type: String,
        required: true,
      },
      fields: {
        type: Array,
        default: null,
      },

    },
    data() {
      return {
        filter: null,
      };
    },
    methods: {
      fetchData(ctx) {
        return this.axios.get(this.apiUrl).then(({data}) => data) || [];
      },

      goToNextEmptyInput(currentElement) {
        let nodes = Array.from(document.querySelectorAll('input')).filter(input => !input.value);
        let nextNode = nodes.find(function(input) {
              return currentElement.compareDocumentPosition(input) & Node.DOCUMENT_POSITION_FOLLOWING;
            });

        if (nextNode) {
          nextNode.focus();
        }
      },

      handleUpdate(event) {
        let input = event.target;
        let form = input.form;

        this.axios.put(form.action, {[input.name]: input.value});
        this.$root.$emit('bv::refresh::table', 'overview-table');
      },
    },
    i18n: {
      messages: {
        de: {
          'Overview': 'Übersicht',
          'component_name': 'Bauelement',
          'project_name': 'Projekt',
          'floor_name': 'Etage',
          'room_name': 'Raum',
          'wall_name': 'Wand',
          'element_name': 'Element',
        },
      },
    },
  };
</script>
